/**
 * Origin of a workspace's frontend, derived by replacing the first subdomain
 * segment of the current hostname with the target workspace's `domain_name`,
 * e.g. `account.ebsio.dev.devebs.net` → `{domainName}.ebsio.dev.devebs.net`.
 */
const workspaceOrigin = (domainName: string): string => {
  const { protocol, hostname, port } = window.location
  const parts = hostname.split('.')
  const baseDomain = parts.length > 1 ? parts.slice(1).join('.') : hostname
  const portSuffix = port ? `:${port}` : ''

  return `${protocol}//${domainName}.${baseDomain}${portSuffix}`
}

/**
 * Per-workspace invitation URL prefix sent to the backend in the `invite_url`
 * field of `/organizations/access/add/` (and resend). The backend appends the
 * invite token, so the result must end with `?token=`.
 */
export const buildWorkspaceInviteUrl = (domainName: string): string =>
  `${workspaceOrigin(domainName)}/authentication/invitation/?token=`

/**
 * Per-workspace password-reset page URL sent in the `redirect_url` field of
 * `/organizations/access/{id}/reset-password/`. The backend appends
 * `?token=<reset-token>`, so the result must NOT already carry a query string.
 */
export const buildWorkspacePasswordResetUrl = (domainName: string): string =>
  `${workspaceOrigin(domainName)}/authentication/reset-password-confirm`

/**
 * Universal, shareable self-service join URL — the workspace's sign-in landing
 * page. When `allow_self_service_join` is enabled, anyone who opens it can
 * request to join / auto-provision (per the workspace's auth policy).
 */
export const buildWorkspaceJoinUrl = (domainName: string): string =>
  `${workspaceOrigin(domainName)}/authentication/login`
